import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Lebensmittelautomat kaufen“ (/lebensmittelautomat).
 * Ziel-Keyword „lebensmittelautomat“ (~600/Monat). Muster/SEO wie Eier-/Hofladen-Seite.
 * Produkt-Constraint: keine Kühlung → nur ungekühlte/lagerfähige Lebensmittel bewerben.
 */
@Component({
  selector: 'app-lebensmittelautomat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './lebensmittelautomat-page.component.html',
  styleUrls: ['./lebensmittelautomat-page.component.scss', '../cluster-landing.shared.scss'],
})
export class LebensmittelautomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogSlug = 'selbstgemachte-lebensmittel-verkaufen-auflagen-und-vorschriften';
  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;
  /** Öffentlicher Live-Link einer Box mit gemischtem Lebensmittelsortiment. */
  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/obsthof-zink/U-MB-00022'
  );

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@lma.faq1.q:Was kostet ein Lebensmittelautomat?`,
      a: $localize`:@@lma.faq1.a:Die marktbox als Lebensmittelautomat gibt es ab 6.800 € zum Kauf oder ab 300 € pro Monat zur Miete – je nach Modell (24 oder 39 Fächer) und Zubehör.`,
    },
    {
      q: $localize`:@@lma.faq2.q:Welche Lebensmittel eignen sich für den Automaten?`,
      a: $localize`:@@lma.faq2.a:Ideal sind ungekühlte, lagerfähige Lebensmittel: Eier, Kartoffeln, Backwaren, Honig, Marmelade, Nudeln, Konserven oder Getränke in Flaschen. Eine Kühlung ist aktuell nicht verfügbar – gekühlte Waren wie Milch, Fleisch oder Käse eignen sich daher noch nicht.`,
    },
    {
      q: $localize`:@@lma.faq3.q:Lebensmittelautomat kaufen oder mieten?`,
      a: $localize`:@@lma.faq3.a:Beides ist möglich. Kaufen lohnt sich bei dauerhaftem Betrieb, Mieten senkt die Einstiegshürde. Eine ausführliche Entscheidungshilfe findest du in unserem Ratgeber.`,
    },
    {
      q: $localize`:@@lma.faq4.q:Brauche ich für den Verkauf von Lebensmitteln eine Genehmigung?`,
      a: $localize`:@@lma.faq4.a:Für viele lagerfähige Lebensmittel ist der Automatenverkauf unkompliziert. Je nach Produkt gelten Kennzeichnungs- und Hygienevorschriften – Details findest du in unserem Ratgeber.`,
    },
  ];

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@lma.metaTitle:Lebensmittelautomat kaufen & mieten | marktbox`;
    const description =
      $localize`:@@lma.metaDesc:Lebensmittelautomat kaufen oder mieten: regionale, lagerfähige Lebensmittel 24/7 verkaufen. marktbox ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content:
        $localize`:@@lma.metaKeywords:Lebensmittelautomat, Lebensmittelautomat kaufen, Automat für Lebensmittel, Lebensmittel verkaufen Automat, Direktvermarktung, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('lebensmittel-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Lebensmittelautomat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('lebensmittel-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/lebensmittelautomat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
